import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import justsatSrc from 'app/assets/images/justsat.png';
import layersStackSrc from 'app/assets/images/layers_stack.png';
import AppNav from 'app/components/AppNav';
import {NavSidebarContainer} from 'app/components/NavSidebar';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {makeUuidPrefix} from 'app/utils/routeUtils';

import AssignedProperties from './myProperties';
import Notifications from './notifications';
import PortfoliosView from './portfolios';
import cs from './styles.module.scss';

const LensHome: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    loggedInUserActions: LoggedInUserActions;
  }>
> = ({organization, profile, loggedInUserActions}) => {
  const [projectsMap, , , {loading, error}] = useProjects();
  const noProjects = !loading && !projectsMap?.size;

  return (
    <>
      <AppNav
        organization={organization}
        profile={profile}
        selectedProject={null}
        loggedInUserActions={loggedInUserActions}
      />
      <NavSidebarContainer>
        {noProjects ? (
          <EmptyHome organization={organization} error={error} />
        ) : (
          <div className={cs.pageContainer}>
            <div className={cs.pageContentContainer}>
              <h1>Home</h1>

              <div className={cs.sectionContainer}>
                <Notifications organization={organization} />
                <AssignedProperties profile={profile} organization={organization} />
              </div>
              <div className={cs.sectionContainer}>
                <PortfoliosView profile={profile} organization={organization} />
              </div>
            </div>
          </div>
        )}
      </NavSidebarContainer>
    </>
  );
};

const EmptyHome: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    error?: boolean;
  }>
> = ({organization, error = false}) => {
  const navigate = useNavigate();
  const orgIdPrefix = makeUuidPrefix(organization.get('id'));

  const EMPTY_STATE_CONTENT = {
    error: {
      header: 'Something went wrong',
      message: 'Please wait for a moment, then try refreshing the page.',
      action: () => {
        window.location.reload();
      },
      actionText: 'Refresh page',
    },
    noProjects: {
      header: 'Welcome to Lens!',
      message: (
        <>
          <div>When your organization adds its first portfolio, you'll see it here.</div>
          <div>Ready to get started?</div>
        </>
      ),
      action: () => {
        navigate(`/${orgIdPrefix}/admin/manageProperties`);
      },
      actionText: 'Add a portfolio',
    },
  };
  const mode = error ? 'error' : 'noProjects';

  return (
    <div className={classnames(cs.emptyState, cs.pageContainer)}>
      <img src={mode === 'error' ? justsatSrc : layersStackSrc} className={cs.emptyStateImage} />
      <div className={cs.emptyStateHeader}>{EMPTY_STATE_CONTENT[mode].header}</div>
      <div>{EMPTY_STATE_CONTENT[mode].message}</div>
      <B.Button
        intent="primary"
        large
        onClick={EMPTY_STATE_CONTENT[mode].action}
        text={EMPTY_STATE_CONTENT[mode].actionText}
      />
    </div>
  );
};

export default LensHome;
